import type { SeriesID } from "../Types/Common/src/ids";
import type { ClinicalParameterValue, FrequencyCurve } from "../Types/Common/src/measurement";
import type { Program } from "../Types/Common/src/program";
import {
  EM_DASH,
  fmtReversibility,
  parsePositiveSetting,
  resolveBdOrientation,
} from "./bdLib";
import { DEFAULT_POST_BD_LABELS, DEFAULT_PRE_BD_LABELS } from "./bdTimerLib";

export type OscBdMetric = "r5" | "x5" | "ax";
export type OscBdDirection = "decrease" | "increase";
export type OscBdChangeColumns = "both" | "absolute" | "percent";
export type OscBdMetricVisibility = Record<OscBdMetric, boolean>;

export interface OscBdMeasurementInput {
  measurementID?: string;
  roundtripTime?: string;
  R5?: ClinicalParameterValue;
  X5?: ClinicalParameterValue;
  AX?: ClinicalParameterValue;
  frequencyCurve?: FrequencyCurve | null;
  rejected?: boolean;
  overread?: { acceptability?: string };
}

export interface OscBdPairingMeasurement extends OscBdMeasurementInput {
  measurementID: string;
  roundtripTime: string;
  bdAdministeredAt?: number;
  linkedBDMeasurementID?: string;
  kioskID?: string;
  kioskSessionID?: string;
  acceptability?: string;
  labels?: Record<string, readonly string[] | undefined>;
}

export interface OscBdLinkedPreResolution<T extends OscBdPairingMeasurement> {
  pre: T | undefined;
  refreshedCurrent: T | undefined;
  linkedMeasurementID: string | undefined;
  explicitLinkObserved: boolean;
}

export const OSC_BD_WINDOW_MS = 4 * 60 * 60 * 1000;
export const OSC_BD_CLUSTER_TOLERANCE_MS = 15 * 60 * 1000;
/** Rows read before an anchor so its round sees the full pool, cluster, and fence. */
export const OSC_BD_READ_MARGIN_MS = 3 * OSC_BD_WINDOW_MS;

export interface OscBdLabelConfig {
  preBdLabels?: readonly string[] | null;
  postBdLabels?: readonly string[] | null;
}

export interface OscBdFallbackEvidence extends OscBdLabelConfig {
  kioskID?: string;
}

export type OscBdRole = "pre" | "post";

export type OscBdUnavailableReason =
  | { kind: "notAcceptable" }
  | { kind: "superseded"; role: OscBdRole; measurementID: string; roundtripTime: string }
  | { kind: "noCompanion"; role: OscBdRole }
  | { kind: "companionNotAcceptable"; role: OscBdRole };

export interface OscBdThresholds {
  r5: number;
  x5: number;
  ax: number;
}

export type OscBdFeatureSet = Pick<
  Program["featureSet"],
  "enablePrePostBd"
  | "enableOscReversibility"
  | "config_OscBdR5Pct"
  | "config_OscBdX5Pct"
  | "config_OscBdAxPct"
  | "config_OscBdMetrics"
  | "config_OscBdChangeColumns"
  | "config_OscBdHideReversibilityVerdict"
  | "config_PreBdLabels"
  | "config_PostBdLabels"
>;

export interface OscBdDisplaySettings {
  enabled: boolean;
  thresholds: OscBdThresholds;
  metrics: OscBdMetricVisibility;
  columns: {
    showAbsolute: boolean;
    showPercent: boolean;
  };
  showVerdict: boolean;
}

export interface OscBdMetricComparison {
  metric: OscBdMetric;
  label: "R5" | "X5" | "AX";
  direction: OscBdDirection;
  pre: number | undefined;
  post: number | undefined;
  unit: string | undefined;
  absoluteChange: number | undefined;
  /** Direction-aware change from baseline. A beneficial change is positive. */
  pctFromBaseline: number | undefined;
  threshold: number;
  reversibilityMet: boolean | undefined;
}

export interface OscBdComparison {
  r5: OscBdMetricComparison;
  x5: OscBdMetricComparison;
  ax: OscBdMetricComparison;
  overallReversibility: boolean | undefined;
}

export interface OscBdComparisonRow extends OscBdMetricComparison {
  formatted: {
    pre: string;
    post: string;
    absoluteChange: string;
    pctFromBaseline: string;
    reversibilityMet: string;
  };
}

/** Renderer-neutral data shared by the kiosk and dashboard tables. */
export interface OscBdComparisonData {
  pre: OscBdMeasurementInput;
  post: OscBdMeasurementInput;
  rows: OscBdComparisonRow[];
  overallReversibility: boolean | undefined;
  formattedOverallReversibility: string;
}

export const DEFAULT_OSC_BD_THRESHOLDS: OscBdThresholds = {
  r5: 0.4,
  x5: 0.5,
  ax: 0.8,
};

export const DEFAULT_OSC_BD_METRICS: OscBdMetricVisibility = {
  r5: true,
  x5: true,
  ax: true,
};

const THRESHOLD_EPSILON = 1e-9;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const normalizeOscBdMeasurementID = (measurementID: string | undefined): string | undefined =>
  measurementID?.replace(/_V[^_]+$/, "_");

export const isOscBdPostMeasurement = (
  measurement: Pick<OscBdPairingMeasurement, "bdAdministeredAt"> | null | undefined,
): boolean => isFiniteNumber(measurement?.bdAdministeredAt);

export const findOscBdMeasurementByID = <T extends OscBdPairingMeasurement>(
  measurements: readonly T[],
  measurementID: string | undefined,
): T | undefined => {
  const normalizedID = normalizeOscBdMeasurementID(measurementID);
  if (!normalizedID) return undefined;
  return measurements.reduce<T | undefined>((latest, candidate) => {
    if (normalizeOscBdMeasurementID(candidate.measurementID) !== normalizedID) return latest;
    return !latest || candidate.measurementID > latest.measurementID ? candidate : latest;
  }, undefined);
};

/** Resolve only the link currently advertised by the latest copy of the post. */
export const resolveOscBdLinkedPre = <T extends OscBdPairingMeasurement>(
  canonicalPost: T,
  recentAverages: readonly T[],
): OscBdLinkedPreResolution<T> => {
  const canonicalID = normalizeOscBdMeasurementID(canonicalPost.measurementID);
  const refreshedCopies = recentAverages.filter(
    measurement => normalizeOscBdMeasurementID(measurement.measurementID) === canonicalID,
  );
  const refreshedCurrent = findOscBdMeasurementByID(recentAverages, canonicalPost.measurementID);
  const linkedMeasurementID = (refreshedCurrent ?? canonicalPost).linkedBDMeasurementID;

  return {
    pre: findOscBdMeasurementByID(recentAverages, linkedMeasurementID),
    refreshedCurrent,
    linkedMeasurementID,
    explicitLinkObserved: !!canonicalPost.linkedBDMeasurementID
      || refreshedCopies.some(measurement => !!measurement.linkedBDMeasurementID),
  };
};

interface OscBdPairingRow<T extends OscBdPairingMeasurement> {
  measurement: T;
  key: string;
  timeMs: number;
  administeredAtMs: number | undefined;
  preOrganizationIDs: string[];
  postOrganizationIDs: string[];
}

interface OscBdRound<T extends OscBdPairingMeasurement> {
  /** One per post cluster; later clusters join when they take the round's pre. */
  administrationsMs: number[];
  posts: OscBdPairingRow<T>[];
  pres: OscBdPairingRow<T>[];
  /** Earlier pres a cluster with none of its own could have taken. */
  borrowablePres?: OscBdPairingRow<T>[];
  chosenPost: OscBdPairingRow<T> | undefined;
  chosenPre: OscBdPairingRow<T> | undefined;
}

interface OscBdLabelTokens {
  pre: string[];
  post: string[];
}

const normalizeLabelTokens = (
  configured: readonly string[] | null | undefined,
  defaults: readonly string[],
): string[] => (configured == null ? defaults : configured)
  .map(label => label.trim().toLowerCase())
  .filter(Boolean);

const toLabelTokens = (labels: OscBdLabelConfig): OscBdLabelTokens => ({
  pre: normalizeLabelTokens(labels.preBdLabels, DEFAULT_PRE_BD_LABELS),
  post: normalizeLabelTokens(labels.postBdLabels, DEFAULT_POST_BD_LABELS),
});

const matchingRoleOrganizationIDs = (
  measurement: OscBdPairingMeasurement,
  tokens: readonly string[],
): string[] => Object.entries(measurement.labels ?? {}).flatMap(([organizationID, labels]) => {
  if (!labels?.length) return [];
  const normalizedLabels = new Set(labels.map(label => label.trim().toLowerCase()));
  return tokens.some(token => normalizedLabels.has(token)) ? [organizationID] : [];
});

const sharesOrganization = (left: readonly string[], right: readonly string[]): boolean => {
  const rightSet = new Set(right);
  return left.some(organizationID => rightSet.has(organizationID));
};

/** Mirrors the backend linker: ingest/waveform quality only, never overreads. */
export const isOscBdAcceptable = (
  measurement: Pick<OscBdPairingMeasurement, "rejected" | "acceptability">,
): boolean => measurement.rejected !== true && measurement.acceptability !== "NotAcceptableAndNotUsable";

const toOscBdPairingRow = <T extends OscBdPairingMeasurement>(
  measurement: T,
  tokens: OscBdLabelTokens,
): OscBdPairingRow<T> | undefined => {
  const key = normalizeOscBdMeasurementID(measurement.measurementID);
  const timeMs = Date.parse(measurement.roundtripTime);
  if (!key || !Number.isFinite(timeMs)) return undefined;
  const administeredAtMs = isFiniteNumber(measurement.bdAdministeredAt)
    ? measurement.bdAdministeredAt
    : undefined;
  return {
    measurement,
    key,
    timeMs,
    administeredAtMs,
    preOrganizationIDs: administeredAtMs === undefined
      ? matchingRoleOrganizationIDs(measurement, tokens.pre)
      : [],
    postOrganizationIDs: administeredAtMs !== undefined && timeMs >= administeredAtMs
      ? matchingRoleOrganizationIDs(measurement, tokens.post)
      : [],
  };
};

/** Latest copy of each OSCAVG recorded on the kiosk. */
const toKioskRows = <T extends OscBdPairingMeasurement>(
  measurements: readonly T[],
  kioskID: string,
  tokens: OscBdLabelTokens,
): OscBdPairingRow<T>[] => {
  const latest = new Map<string, T>();
  measurements.forEach(measurement => {
    const key = normalizeOscBdMeasurementID(measurement.measurementID);
    const existing = key ? latest.get(key) : undefined;
    if (key && (!existing || measurement.measurementID > existing.measurementID)) {
      latest.set(key, measurement);
    }
  });
  return [...latest.values()]
    .filter(measurement => measurement.kioskID === kioskID)
    .map(measurement => toOscBdPairingRow(measurement, tokens))
    .filter((row): row is OscBdPairingRow<T> => !!row);
};

const isRowAcceptable = <T extends OscBdPairingMeasurement>({ measurement }: OscBdPairingRow<T>): boolean =>
  isOscBdAcceptable(measurement);

/** Negative when `left` is the more recent row. */
const compareLatestFirst = <T extends OscBdPairingMeasurement>(
  left: OscBdPairingRow<T>,
  right: OscBdPairingRow<T>,
): number => right.timeMs - left.timeMs
  || right.measurement.measurementID.localeCompare(left.measurement.measurementID);

const pickLatest = <T extends OscBdPairingMeasurement>(
  rows: OscBdPairingRow<T>[],
): OscBdPairingRow<T> | undefined => [...rows].sort(compareLatestFirst)[0];

const isSameKioskSession = <T extends OscBdPairingMeasurement>(
  left: OscBdPairingRow<T>,
  right: OscBdPairingRow<T>,
): boolean => !!left.measurement.kioskSessionID
  && left.measurement.kioskSessionID === right.measurement.kioskSessionID;

const clusterOscBdPosts = <T extends OscBdPairingMeasurement>(
  posts: OscBdPairingRow<T>[],
): OscBdPairingRow<T>[][] => {
  const sorted = [...posts].sort((left, right) => left.administeredAtMs! - right.administeredAtMs!);
  const clusters: OscBdPairingRow<T>[][] = [];

  sorted.forEach(post => {
    const current = clusters[clusters.length - 1];
    if (current && post.administeredAtMs! - current[0].administeredAtMs! <= OSC_BD_CLUSTER_TOLERANCE_MS) {
      current.push(post);
    } else {
      clusters.push([post]);
    }
  });

  return clusters;
};

/**
 * Backend round parity for one kiosk: a post cluster plus the pres in
 * `[max(administration - 4h, previous administration), administration)`.
 * A cluster with no pres of its own joins the latest round with pres when that round has an
 * acceptable pre within 4h of the cluster's administration; the later post takes the pair.
 * KioskSession evidence isn't visible here, so session-excluded rows still count.
 */
const buildOscBdRounds = <T extends OscBdPairingMeasurement>(
  rows: OscBdPairingRow<T>[],
): OscBdRound<T>[] => {
  const pres = rows.filter(row => row.preOrganizationIDs.length > 0);
  const rounds: OscBdRound<T>[] = [];
  let latestRoundWithPres: OscBdRound<T> | undefined;
  let previousAdministrationMs = -Infinity;

  for (const posts of clusterOscBdPosts(rows.filter(row => row.postOrganizationIDs.length > 0))) {
    const administrationMs = posts[0].administeredAtMs!;
    const floorMs = Math.max(administrationMs - OSC_BD_WINDOW_MS, previousAdministrationMs);
    previousAdministrationMs = administrationMs;

    const memberPres = pres.filter(pre =>
      pre.timeMs >= floorMs
      && pre.timeMs < administrationMs
      && posts.some(post => sharesOrganization(pre.preOrganizationIDs, post.postOrganizationIDs))
    );
    const chosenPost = pickLatest(posts.filter(isRowAcceptable));
    const pickPre = (candidates: OscBdPairingRow<T>[]) => chosenPost && pickLatest(candidates.filter(pre =>
      isRowAcceptable(pre)
      && !isSameKioskSession(pre, chosenPost)
      && sharesOrganization(pre.preOrganizationIDs, chosenPost.postOrganizationIDs)
    ));

    if (memberPres.length) {
      latestRoundWithPres = {
        administrationsMs: [administrationMs],
        posts: [...posts],
        pres: memberPres,
        chosenPost,
        chosenPre: pickPre(memberPres),
      };
      rounds.push(latestRoundWithPres);
      continue;
    }

    const borrowablePres = latestRoundWithPres?.pres.filter(pre =>
      pre.timeMs >= administrationMs - OSC_BD_WINDOW_MS
      && posts.some(post => sharesOrganization(pre.preOrganizationIDs, post.postOrganizationIDs))
    ) ?? [];
    const borrowedPre = pickPre(borrowablePres);
    if (latestRoundWithPres && borrowedPre) {
      latestRoundWithPres.administrationsMs.push(administrationMs);
      latestRoundWithPres.posts.push(...posts);
      latestRoundWithPres.chosenPost = chosenPost;
      latestRoundWithPres.chosenPre = borrowedPre;
      continue;
    }
    rounds.push({ administrationsMs: [administrationMs], posts, pres: [], borrowablePres, chosenPost, chosenPre: undefined });
  }
  return rounds;
};

const roleOfRow = <T extends OscBdPairingMeasurement>(row: OscBdPairingRow<T>): OscBdRole | undefined =>
  row.postOrganizationIDs.length > 0 ? "post" : row.preOrganizationIDs.length > 0 ? "pre" : undefined;

/** Pre/post role from the administration stamp and configured BD labels. */
export const resolveOscBdRole = (
  measurement: OscBdPairingMeasurement,
  labels: OscBdLabelConfig = {},
): OscBdRole | undefined => {
  const row = toOscBdPairingRow(measurement, toLabelTokens(labels));
  return row && roleOfRow(row);
};

/**
 * Resolve a kiosk-only fallback while the backend link is still propagating:
 * the pre the backend reconciler pairs with this post, or nothing when the
 * post is not its round's latest acceptable series.
 */
export const resolveOscBdAdministrationFallbackPre = <T extends OscBdPairingMeasurement>(
  canonicalPost: T,
  recentAverages: readonly T[],
  evidence: OscBdFallbackEvidence = {},
): T | undefined => {
  const kioskID = evidence.kioskID;
  if (!isOscBdPostMeasurement(canonicalPost) || !kioskID || canonicalPost.kioskID !== kioskID) return undefined;

  const canonicalKey = normalizeOscBdMeasurementID(canonicalPost.measurementID);
  const round = buildOscBdRounds(toKioskRows([...recentAverages, canonicalPost], kioskID, toLabelTokens(evidence)))
    .find(candidate => candidate.posts.some(post => post.key === canonicalKey));

  return round && round.chosenPost?.key === canonicalKey ? round.chosenPre?.measurement : undefined;
};

const supersededBy = <T extends OscBdPairingMeasurement>(
  role: OscBdRole,
  row: OscBdPairingRow<T>,
): OscBdUnavailableReason => ({
  kind: "superseded",
  role,
  measurementID: row.key,
  roundtripTime: row.measurement.roundtripTime,
});

/** Why a BD series has no comparison, from its kiosk's OSCAVG rows; undefined means generic notice only. */
export const resolveOscBdUnavailableReason = <T extends OscBdPairingMeasurement>(
  viewed: T,
  measurements: readonly T[],
  labels: OscBdLabelConfig = {},
): OscBdUnavailableReason | undefined => {
  const tokens = toLabelTokens(labels);
  const current = findOscBdMeasurementByID([...measurements, viewed], viewed.measurementID) ?? viewed;
  const currentRow = toOscBdPairingRow(current, tokens);
  const role = currentRow && roleOfRow(currentRow);
  if (!role) return undefined;
  if (!isOscBdAcceptable(current)) return { kind: "notAcceptable" };
  if (!current.kioskID) return undefined;

  const rows = toKioskRows([...measurements, current], current.kioskID, tokens);
  const viewedRow = rows.find(row => row.key === currentRow.key);
  if (!viewedRow) return undefined;
  const rounds = buildOscBdRounds(rows);

  if (role === "post") {
    const round = rounds.find(candidate => candidate.posts.includes(viewedRow));
    if (!round) return undefined;
    if (round.chosenPost && round.chosenPost !== viewedRow) return supersededBy("post", round.chosenPost);
    if (round.chosenPre) return undefined;
    const companions = round.pres.length ? round.pres : round.borrowablePres ?? [];
    if (!companions.length) return { kind: "noCompanion", role: "post" };
    return companions.some(isRowAcceptable) ? undefined : { kind: "companionNotAcceptable", role: "post" };
  }

  const round = rounds.find(candidate => candidate.pres.includes(viewedRow));
  if (round) {
    if (!round.chosenPost) return { kind: "companionNotAcceptable", role: "pre" };
    return round.chosenPre && round.chosenPre !== viewedRow && compareLatestFirst(round.chosenPre, viewedRow) < 0
      ? supersededBy("pre", round.chosenPre)
      : undefined;
  }

  // No administration within 4h follows this pre: a newer pre before the next one supersedes it.
  const nextAdministrationMs = Math.min(...rounds
    .flatMap(candidate => candidate.administrationsMs)
    .filter(administrationMs => administrationMs > viewedRow.timeMs));
  const newerPre = pickLatest(rows.filter(row =>
    row !== viewedRow
    && isRowAcceptable(row)
    && sharesOrganization(row.preOrganizationIDs, viewedRow.preOrganizationIDs)
    && compareLatestFirst(row, viewedRow) < 0
    && row.timeMs - viewedRow.timeMs <= OSC_BD_WINDOW_MS
    && row.timeMs < nextAdministrationMs
  ));
  return newerPre ? supersededBy("pre", newerPre) : { kind: "noCompanion", role: "pre" };
};

export interface OscBdUnavailableText {
  summary: string;
  detail: string | undefined;
}

export const formatOscBdUnavailableText = (
  reason: OscBdUnavailableReason | undefined,
  translations: Record<string, string | undefined> = {},
): OscBdUnavailableText => {
  const summary = translations.oscBdTableUnavailable || "The BD table is not available.";
  if (reason?.kind === "notAcceptable") {
    return {
      summary,
      detail: translations.oscBdSeriesBelowStudyMinimum
        || "The current series does not meet the study minimum quality criteria.",
    };
  }
  if (reason?.kind === "noCompanion" && reason.role === "post") {
    return {
      summary,
      detail: translations.oscBdNoCorrespondingPreBd || "There is no corresponding preBD series.",
    };
  }
  if (reason?.kind === "superseded" && reason.role === "post") {
    return {
      summary,
      detail: translations.oscBdMoreRecentPostBd || "There is a more recent postBD series. Please navigate to:",
    };
  }
  // Reasons without agreed wording show the summary alone.
  return { summary, detail: undefined };
};

const clinicalValue = (parameter: ClinicalParameterValue | undefined): number | undefined =>
  isFiniteNumber(parameter?.value) ? parameter.value : undefined;

const x5FromCurve = (frequencyCurve: FrequencyCurve | null | undefined): number | undefined => {
  const point = frequencyCurve?.X?.find(({ frequency }) => frequency === 5);
  return isFiniteNumber(point?.value) ? point.value : undefined;
};

/** An existing X5 field is authoritative; only historical rows without it use the curve. */
export const extractOscBdValues = (
  measurement: OscBdMeasurementInput | null | undefined,
): { r5: number | undefined; x5: number | undefined; ax: number | undefined } => {
  if (measurement?.rejected === true || measurement?.overread?.acceptability === "Reject") {
    return { r5: undefined, x5: undefined, ax: undefined };
  }
  return {
    r5: clinicalValue(measurement?.R5),
    x5: measurement?.X5 === undefined
      ? x5FromCurve(measurement?.frequencyCurve)
      : clinicalValue(measurement.X5),
    ax: clinicalValue(measurement?.AX),
  };
};

export const resolveOscBdDisplaySettings = (
  featureSet?: OscBdFeatureSet | null,
): OscBdDisplaySettings => {
  const fs = featureSet ?? {};
  const threshold = (value: number | string | null | undefined, fallback: number): number => {
    const parsed = parsePositiveSetting(value);
    return parsed === undefined || !Number.isInteger(parsed) || parsed > 100
      ? fallback
      : parsed / 100;
  };
  const selectedMetrics = Array.isArray(fs.config_OscBdMetrics)
    ? new Set(fs.config_OscBdMetrics.filter(
      (metric): metric is OscBdMetric => metric === "r5" || metric === "x5" || metric === "ax",
    ))
    : undefined;
  const metrics = selectedMetrics?.size
    ? {
      r5: selectedMetrics.has("r5"),
      x5: selectedMetrics.has("x5"),
      ax: selectedMetrics.has("ax"),
    }
    : { ...DEFAULT_OSC_BD_METRICS };
  const changeColumns: OscBdChangeColumns = fs.config_OscBdChangeColumns === "absolute"
    || fs.config_OscBdChangeColumns === "percent"
    ? fs.config_OscBdChangeColumns
    : "both";

  return {
    enabled: !!fs.enablePrePostBd && !!fs.enableOscReversibility,
    thresholds: {
      r5: threshold(fs.config_OscBdR5Pct, DEFAULT_OSC_BD_THRESHOLDS.r5),
      x5: threshold(fs.config_OscBdX5Pct, DEFAULT_OSC_BD_THRESHOLDS.x5),
      ax: threshold(fs.config_OscBdAxPct, DEFAULT_OSC_BD_THRESHOLDS.ax),
    },
    metrics,
    columns: {
      showAbsolute: changeColumns !== "percent",
      showPercent: changeColumns !== "absolute",
    },
    showVerdict: !fs.config_OscBdHideReversibilityVerdict,
  };
};

export const DEFAULT_OSC_BD_DISPLAY_SETTINGS: OscBdDisplaySettings = resolveOscBdDisplaySettings();

interface MetricDefinition {
  metric: OscBdMetric;
  label: OscBdMetricComparison["label"];
  direction: OscBdDirection;
}

const METRICS: MetricDefinition[] = [
  { metric: "r5", label: "R5", direction: "decrease" },
  { metric: "x5", label: "X5", direction: "increase" },
  { metric: "ax", label: "AX", direction: "decrease" },
];

const compareMetric = (
  definition: MetricDefinition,
  pre: OscBdMeasurementInput,
  post: OscBdMeasurementInput,
  thresholds: OscBdThresholds,
): OscBdMetricComparison => {
  const preValues = extractOscBdValues(pre);
  const postValues = extractOscBdValues(post);
  const preValue = preValues[definition.metric];
  const postValue = postValues[definition.metric];
  const preParameter = definition.metric === "r5" ? pre.R5 : definition.metric === "x5" ? pre.X5 : pre.AX;
  const postParameter = definition.metric === "r5" ? post.R5 : definition.metric === "x5" ? post.X5 : post.AX;
  const unit = preParameter?.unit ?? postParameter?.unit
    ?? (definition.metric === "x5"
      ? pre.frequencyCurve?.X?.find(({ frequency }) => frequency === 5)?.unit
        ?? post.frequencyCurve?.X?.find(({ frequency }) => frequency === 5)?.unit
      : undefined);
  const threshold = thresholds[definition.metric];

  if (!isFiniteNumber(preValue) || !isFiniteNumber(postValue)) {
    return {
      ...definition,
      pre: preValue,
      post: postValue,
      unit,
      absoluteChange: undefined,
      pctFromBaseline: undefined,
      threshold,
      reversibilityMet: undefined,
    };
  }

  const absoluteChange = postValue - preValue;
  const directionalChange = definition.direction === "increase" ? absoluteChange : -absoluteChange;
  const pctFromBaseline = preValue === 0 ? undefined : directionalChange / Math.abs(preValue);

  return {
    ...definition,
    pre: preValue,
    post: postValue,
    unit,
    absoluteChange,
    pctFromBaseline,
    threshold,
    reversibilityMet: pctFromBaseline === undefined
      ? undefined
      : pctFromBaseline >= threshold - THRESHOLD_EPSILON,
  };
};

export const computeOscBdComparison = (
  pre: OscBdMeasurementInput,
  post: OscBdMeasurementInput,
  thresholds: OscBdThresholds = DEFAULT_OSC_BD_THRESHOLDS,
): OscBdComparison => {
  const rows = METRICS.map((definition) => compareMetric(definition, pre, post, thresholds));
  const [r5, x5, ax] = rows;
  const verdicts = rows
    .map(({ reversibilityMet }) => reversibilityMet)
    .filter((verdict): verdict is boolean => verdict !== undefined);

  return {
    r5,
    x5,
    ax,
    overallReversibility: verdicts.length === 0 ? undefined : verdicts.some(Boolean),
  };
};

const fmtValue = (value: number | undefined): string =>
  value === undefined ? EM_DASH : value.toFixed(2);

const fmtPctChange = (value: number | undefined): string =>
  value === undefined ? EM_DASH : `${(value * 100).toFixed(2)}%`;

const buildRow = (comparison: OscBdMetricComparison): OscBdComparisonRow => {
  const pre = fmtValue(comparison.pre);
  const post = fmtValue(comparison.post);
  return {
    ...comparison,
    formatted: {
      pre,
      post,
      // Match the displayed operands; percentages and verdicts retain full precision.
      absoluteChange: comparison.absoluteChange === undefined
        ? EM_DASH
        : fmtValue(Number(post) - Number(pre)),
      pctFromBaseline: fmtPctChange(comparison.pctFromBaseline),
      reversibilityMet: fmtReversibility(comparison.reversibilityMet),
    },
  };
};

const toOrientable = (measurement: OscBdMeasurementInput) => {
  const timestamp = measurement.roundtripTime ? Date.parse(measurement.roundtripTime) : NaN;
  return {
    seriesID: measurement.measurementID as SeriesID | undefined,
    created: Number.isFinite(timestamp) ? timestamp : undefined,
    measurement,
  };
};

/** Orient a linked pair from roundtripTime and build renderer-neutral rows. */
export const buildOscBdComparisonData = (
  viewed: OscBdMeasurementInput,
  companion: OscBdMeasurementInput,
  thresholds: OscBdThresholds = DEFAULT_OSC_BD_THRESHOLDS,
  metrics: OscBdMetricVisibility = DEFAULT_OSC_BD_METRICS,
): OscBdComparisonData => {
  const oriented = resolveBdOrientation(toOrientable(viewed), toOrientable(companion));
  const pre = oriented.pre.measurement;
  const post = oriented.post.measurement;
  const comparison = computeOscBdComparison(pre, post, thresholds);
  const visibleComparisons = [comparison.r5, comparison.x5, comparison.ax]
    .filter(row => metrics[row.metric]);
  const verdicts = visibleComparisons
    .map(({ reversibilityMet }) => reversibilityMet)
    .filter((verdict): verdict is boolean => verdict !== undefined);
  const overallReversibility = verdicts.length === 0 ? undefined : verdicts.some(Boolean);

  return {
    pre,
    post,
    rows: visibleComparisons.map(buildRow),
    overallReversibility,
    formattedOverallReversibility: fmtReversibility(overallReversibility),
  };
};
